import React from "react";
import { Box, Typography, IconButton, CircularProgress } from "@mui/material";
import { MdOutlineDelete, MdOutlineVisibility, MdRefresh } from "react-icons/md";
import { FaFilePdf, FaFileExcel, FaFileWord } from "react-icons/fa";
import { MdImage } from "react-icons/md";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatFileSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileExtension = (fileName = "") =>
  fileName.split(".").pop()?.toLowerCase() ?? "";

// ---------------------------------------------------------------------------
// FileTypeIcon – small coloured icon matching the Figma file cards
// ---------------------------------------------------------------------------

function FileTypeIcon({ fileName }) {
  const ext = getFileExtension(fileName);

  if (ext === "pdf") {
    return (
      <Box
        sx={{
          width: 44,
          height: 50,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#fdecea",
          borderRadius: "6px",
          flexShrink: 0,
        }}
      >
        <FaFilePdf size={28} color="#e53935" />
      </Box>
    );
  }

  if (ext === "xlsx" || ext === "xls") {
    return (
      <Box
        sx={{
          width: 44,
          height: 50,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#e8f5e9",
          borderRadius: "6px",
          flexShrink: 0,
        }}
      >
        <FaFileExcel size={28} color="#1b5e20" />
      </Box>
    );
  }

  if (ext === "doc" || ext === "docx") {
    return (
      <Box
        sx={{
          width: 44,
          height: 50,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#e3f2fd",
          borderRadius: "6px",
          flexShrink: 0,
        }}
      >
        <FaFileWord size={28} color="#1565c0" />
      </Box>
    );
  }

  // PNG / JPG / JPEG / other images
  return (
    <Box
      sx={{
        width: 44,
        height: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#e3f2fd",
        borderRadius: "6px",
        flexShrink: 0,
      }}
    >
      <MdImage size={30} color="#1565c0" />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// FilePreviewCard
// ---------------------------------------------------------------------------

/**
 * FilePreviewCard
 *
 * Shown after a file is selected/uploaded. Mirrors the Figma card exactly:
 *  - File type icon (left)
 *  - File name + size + status label (center)
 *  - Action buttons: View + Delete (success) | Retry (failed)
 *
 * Props:
 *  @param {File}     file      - raw File object
 *  @param {string}   status    - "uploading" | "success" | "failed"
 *  @param {string}   fileUrl   - blob/server URL for preview (null while uploading)
 *  @param {function} onDelete  - called when delete button clicked
 *  @param {function} onView    - called when view button clicked
 *  @param {function} onRetry   - called when retry button clicked
 *  @param {boolean}  disabled  - disables all actions (timer expired)
 */
export default function FilePreviewCard({
  file,
  status,
  fileUrl,
  onDelete,
  onView,
  onRetry,
  disabled,
}) {
  const isUploading = status === "uploading";
  const isSuccess = status === "success";
  const isFailed = status === "failed";
  const isDeleting = status === "deleting";

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1.5,
        p: "12px 14px",
        border: "1px solid #e0e0e0",
        borderRadius: "10px",
        backgroundColor: "#fafafa",
        width: { xs: "100%", sm: "fit-content" },
        minWidth: { sm: "320px" },
        maxWidth: "420px",
      }}
    >
      {/* ── File icon ────────────────────────────────── */}
      <FileTypeIcon fileName={file?.name} />

      {/* ── File info ────────────────────────────────── */}
      <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: "#1e1e1e",
          }}
          title={file?.name}
        >
          {file?.name}
        </Typography>

        <Typography sx={{ fontSize: "12px", color: "gray", mb: 0.4 }}>
          {formatFileSize(file?.size)}
        </Typography>

        {/* Status label */}
        {isUploading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <CircularProgress size={11} sx={{ color: "#3F41D1" }} />
            <Typography sx={{ fontSize: "11px", color: "#3F41D1" }}>
              Uploading…
            </Typography>
          </Box>
        )}

        {(isSuccess || isDeleting) && (
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: "bold",
              color: "#2e7d32",
              letterSpacing: "0.4px",
              textTransform: "uppercase",
            }}
          >
            Uploaded Successfully
          </Typography>
        )}

        {isFailed && (
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: "bold",
              color: "#c62828",
              letterSpacing: "0.4px",
              textTransform: "uppercase",
            }}
          >
            Uploaded Failed
          </Typography>
        )}
      </Box>

      {/* ── Action buttons ───────────────────────────── */}
      <Box sx={{ display: "flex", gap: 0.75, flexShrink: 0, alignItems: "center" }}>
        {(isSuccess || isDeleting) && (
          <>
            {/* View */}
            <IconButton
              size="small"
              onClick={onView}
              disabled={disabled || isDeleting}
              title="View file"
              sx={{
                width: 34,
                height: 34,
                backgroundColor: "rgba(63,65,209,0.12)",
                color: "#3F41D1",
                "&:hover": { backgroundColor: "rgba(63,65,209,0.22)" },
                "&.Mui-disabled": { opacity: 0.4 },
              }}
            >
              <MdOutlineVisibility size={18} />
            </IconButton>

            {/* Delete */}
            <IconButton
              size="small"
              onClick={onDelete}
              disabled={disabled || isDeleting}
              title="Delete file"
              sx={{
                width: 34,
                height: 34,
                backgroundColor: "#e53935",
                color: "white",
                "&:hover": { backgroundColor: "#c62828" },
                "&.Mui-disabled": { opacity: 0.4 },
              }}
            >
              {isDeleting ? (
                <CircularProgress size={14} sx={{ color: "white" }} />
              ) : (
                <MdOutlineDelete size={18} />
              )}
            </IconButton>
          </>
        )}

        {isFailed && (
          /* Retry – plain icon, no background circle, matching Figma */
          <IconButton
            size="small"
            onClick={onRetry}
            disabled={disabled}
            title="Retry upload"
            sx={{
              width: 34,
              height: 34,
              color: "#3F41D1",
              "&:hover": { backgroundColor: "rgba(63,65,209,0.1)" },
              "&.Mui-disabled": { opacity: 0.4 },
            }}
          >
            <MdRefresh size={22} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}
